import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  fetchCurrencies,
  fetchExchangeAmount,
  createTransaction,
  fetchTransaction,
  fetchTransactions,
  validateAddress,
  fetchEstimatedArrivalTime,
  fetchMarketData,
  fetchLivePrices,
  fetchTokenPrice,
} from '../services/api';
import { useWebSocket } from '../context/WebSocketContext.jsx';

// Re-export useLiveMarketData for convenience
export { useLiveMarketData } from './useLiveMarketData';

// Hook for currencies with caching
export const useCurrencies = () => {
  return useQuery('currencies', fetchCurrencies, {
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });
};

// Hook for exchange rate with auto-refresh
export const useExchangeRate = (from, to, amount, arbitrage = false) => {
  return useQuery(
    ['exchangeRate', from, to, amount, arbitrage],
    () => fetchExchangeAmount(from, to, amount, arbitrage),
    {
      enabled: !!from && !!to && !!amount && parseFloat(amount) > 0,
      refetchInterval: 30000,
      staleTime: 15000,
    }
  );
};

// Hook for estimated time
export const useEstimatedTime = (from, to, arbitrage = false) => {
  return useQuery(
    ['estimatedTime', from, to, arbitrage],
    () => fetchEstimatedArrivalTime(from, to, arbitrage),
    {
      enabled: !!from && !!to,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// Hook for creating transactions
export const useCreateTransaction = () => {
  return useMutation(createTransaction);
};

// Hook for transaction status with polling and WebSocket
export const useTransaction = (id) => {
  const queryClient = useQueryClient();
  const { lastMessage, subscribeToTransaction, unsubscribeFromTransaction } = useWebSocket();

  // Subscribe to WebSocket updates for this transaction
  useEffect(() => {
    if (!id) return;
    
    subscribeToTransaction(id);
    
    return () => {
      unsubscribeFromTransaction(id);
    };
  }, [id, subscribeToTransaction, unsubscribeFromTransaction]);

  // Handle WebSocket messages - force immediate refetch
  useEffect(() => {
    if (!lastMessage || !id) return;
    
    console.log('useTransaction received message:', lastMessage);
    
    if (lastMessage.type === 'transaction:updated' && lastMessage.transaction?.id === id) {
      console.log('Status changed to:', lastMessage.transaction.status, '- triggering refetch');
      // Cancel any ongoing queries and refetch immediately
      queryClient.cancelQueries(['transaction', id]);
      queryClient.invalidateQueries(['transaction', id], { exact: true });
      // Force immediate refetch
      queryClient.refetchQueries(['transaction', id], { exact: true, active: true });
    }
  }, [lastMessage, id, queryClient]);

  return useQuery(
    ['transaction', id],
    () => fetchTransaction(id),
    {
      enabled: !!id,
      refetchInterval: (data) => {
        if (!data) return 5000; // Poll every 5s while loading
        const status = data.status;
        // Poll active transactions more frequently
        if (['waiting', 'confirming', 'exchanging', 'sending'].includes(status)) {
          return 5000; // 5 seconds for active transactions
        }
        // Poll completed/failed transactions less frequently
        if (['completed', 'finished', 'failed', 'refunded'].includes(status)) {
          return 30000; // 30 seconds for final states
        }
        return 10000; // Default 10 seconds
      },
      staleTime: 0, // Always consider data stale (for immediate updates)
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    }
  );
};

// Hook for address validation with debounce
export const useAddressValidation = (currency, address, extraId) => {
  const [validation, setValidation] = useState({ isValid: null, message: null });
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!address) {
      setValidation({ isValid: null, message: null });
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        const result = await validateAddress(currency, address, extraId);
        setValidation(result);
      } catch (error) {
        setValidation({ isValid: false, message: error.message });
      }
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currency, address, extraId]);

  return validation;
};

// Hook for market data
export const useMarketData = () => {
  return useQuery('marketData', fetchMarketData, {
    refetchInterval: 60000,
    staleTime: 30000,
  });
};

// Hook for live prices (array format with images)
export const useLivePrices = () => {
  return useQuery('livePrices', fetchLivePrices, {
    refetchInterval: 60000,
    staleTime: 30000,
  });
};

// Hook for individual token price
export const useTokenPrice = (coinId) => {
  return useQuery(
    ['tokenPrice', coinId],
    () => fetchTokenPrice(coinId),
    {
      enabled: !!coinId,
      refetchInterval: 60000,
      staleTime: 30000,
    }
  );
};

// Hook for exchange form state management
export const useExchangeForm = () => {
  const [fromCurrency, setFromCurrency] = useState(null);
  const [toCurrency, setToCurrency] = useState(null);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [recipientExtraId, setRecipientExtraId] = useState('');
  const [refundAddress, setRefundAddress] = useState('');
  const [refundExtraId, setRefundExtraId] = useState('');
  const [arbitrageEnabled, setArbitrageEnabled] = useState(false);

  const { data: rateData, isLoading: isRateLoading } = useExchangeRate(
    fromCurrency?.ticker,
    toCurrency?.ticker,
    fromAmount,
    arbitrageEnabled
  );

  const { data: estimatedTimeData } = useEstimatedTime(
    fromCurrency?.ticker,
    toCurrency?.ticker,
    arbitrageEnabled
  );

  // Update toAmount when rate changes
  useEffect(() => {
    if (rateData?.estimatedAmount) {
      setToAmount(rateData.estimatedAmount);
    }
  }, [rateData]);

  // Swap currencies
  const swapCurrencies = useCallback(() => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setFromAmount(toAmount);
  }, [fromCurrency, toCurrency, toAmount]);

  // Reset form
  const resetForm = useCallback(() => {
    setFromAmount('');
    setToAmount('');
    setRecipientAddress('');
    setRecipientExtraId('');
    setRefundAddress('');
    setRefundExtraId('');
  }, []);

  return {
    // State
    fromCurrency,
    toCurrency,
    fromAmount,
    toAmount,
    recipientAddress,
    recipientExtraId,
    refundAddress,
    refundExtraId,
    arbitrageEnabled,
    
    // Setters
    setFromCurrency,
    setToCurrency,
    setFromAmount,
    setToAmount,
    setRecipientAddress,
    setRecipientExtraId,
    setRefundAddress,
    setRefundExtraId,
    setArbitrageEnabled,
    
    // Data
    rateData,
    isRateLoading,
    estimatedTime: estimatedTimeData?.estimatedTime,
    
    // Actions
    swapCurrencies,
    resetForm,
  };
};

// Hook for transaction history from localStorage (legacy support)
export const useTransactionHistory = () => {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('transactionHistory');
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse transaction history', e);
      }
    }
  }, []);

  const addTransaction = useCallback((transaction) => {
    setHistory((prev) => {
      const newHistory = [transaction, ...prev].slice(0, 50);
      localStorage.setItem('transactionHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem('transactionHistory');
  }, []);

  return { history, addTransaction, clearHistory };
};

// Hook for fetching transactions from server (for both users and guests)
export const useTransactions = (options = {}) => {
  return useQuery(
    ['transactions', options],
    () => fetchTransactions(options),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      staleTime: 15000,
      keepPreviousData: true,
    }
  );
};

export default useExchangeForm;
